<script lang="ts">
	import { afterNavigate, goto } from '$app/navigation';
	import { Button } from '$lib/components/ui/button/index.js';
	import { Checkbox } from '$lib/components/ui/checkbox/index.js';
	import { Label } from '$lib/components/ui/label/index.js';
	import * as Popover from '$lib/components/ui/popover/index.js';
	import { RangeCalendar } from '$lib/components/ui/range-calendar/index.js';
	import * as Select from '$lib/components/ui/select/index.js';
	import * as Sidebar from '$lib/components/ui/sidebar/index.js';
	import { useSidebar } from '$lib/components/ui/sidebar/index.js';
	import { Slider } from '$lib/components/ui/slider/index.js';
	import * as ToggleGroup from '$lib/components/ui/toggle-group/index.js';
	import { buildBarcosPath, comunidadBySlug, COMUNIDADES, type Comunidad } from '$lib/geo';
	import { EXPERIENCES_FILTER_HELP, type ListingTypeFilter } from '$lib/listing-display';
	import { cn } from '$lib/utils.js';
	import { localizeHref } from '@/paraglide/runtime';
	import { getMarketplaceFilters } from '@/stores/MarketplaceFilters.svelte';
	import { getPortsState } from '@/stores/Port.svelte';
	import { DateFormatter, type DateValue, getLocalTimeZone } from '@internationalized/date';
	import RotateCcw from '@lucide/svelte/icons/rotate-ccw';
	import Sailboat from '@lucide/svelte/icons/sailboat';
	import Ship from '@lucide/svelte/icons/ship';
	import CalendarIcon from '@lucide/svelte/icons/calendar';
	import CircleQuestionMark from '@lucide/svelte/icons/circle-question-mark';
	import { type ComponentProps, onDestroy } from 'svelte';

	let className: string | null | undefined = $state(undefined);
	export { className as class };

	const ports = getPortsState();
	const filters = getMarketplaceFilters();

	$effect(() => {
		ports.fetchPorts();
	});

	// Ubicación en cascada: Comunidad → Provincia → Ciudad, sincronizada con
	// los segmentos del path /barcos/...
	const ALL = '__all__';

	const provincesWithPorts = $derived(new Set(ports.ports.map((port) => port.province.trim())));

	const comunidadOptions = $derived(
		COMUNIDADES.filter((comunidad) =>
			comunidad.provinces.some((province) => provincesWithPorts.has(province))
		)
	);

	const selectedComunidad = $derived(filters.comunidad);

	const provinciaOptions = $derived(
		selectedComunidad && !selectedComunidad.uniprovincial
			? selectedComunidad.provinces.filter((province) => provincesWithPorts.has(province))
			: []
	);

	const cityOptions = $derived.by(() => {
		if (!selectedComunidad) return [];
		const inProvinces: string[] = filters.province
			? [filters.province]
			: selectedComunidad.uniprovincial
				? [...selectedComunidad.provinces]
				: [];
		if (inProvinces.length === 0) return [];
		const cities = ports.ports
			.filter((port) => inProvinces.includes(port.province.trim()) && port.city?.trim())
			.map((port) => port.city!.trim());
		return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'es'));
	});

	const portOptions = $derived.by(() => {
		if (!filters.city) return [];
		return ports.ports
			.filter((port) => port.city?.trim() === filters.city)
			.map((port) => port.name.trim())
			.sort((a, b) => a.localeCompare(b, 'es'));
	});

	function locationQueryString(): string {
		const params = filters.toURLParams();
		params.delete('location');
		params.delete('province');
		const query = params.toString();
		return query ? `?${query}` : '';
	}

	function navigateToLocation(
		comunidad: Comunidad | null,
		provincia?: string | null,
		ciudad?: string | null,
		puerto?: string | null
	) {
		const path = buildBarcosPath({
			tipo: filters.routeTipo,
			comunidad,
			provincia: comunidad?.uniprovincial ? comunidad.provinces[0] : provincia,
			ciudad,
			puerto
		});
		goto(localizeHref(path) + locationQueryString());
	}

	const df = new DateFormatter('es-ES', {
		dateStyle: 'short'
	});

	let startValue: DateValue | undefined = $state(undefined);
	let {
		ref = $bindable(null),
		collapsible = 'icon',
		...restProps
	}: ComponentProps<typeof Sidebar.Root> = $props();

	const sidebar = useSidebar();

	// Sin rail de colapso: mantener el sidebar expandido en desktop si quedó colapsado.
	$effect(() => {
		if (!sidebar.isMobile && !sidebar.open) {
			sidebar.setOpen(true);
		}
	});

	afterNavigate(() => {
		if (sidebar.isMobile) {
			sidebar.setOpenMobile(false);
		}
	});

	function handleApplyFilters() {
		filters.applyFilters();
	}

	function handleResetFilters() {
		filters.reset();
		goto(localizeHref('/barcos'));
	}

	onDestroy(() => {
		filters.destroy();
	});
</script>

<Sidebar.Root bind:ref={ref} collapsible={collapsible} {...restProps}>
	<Sidebar.Content>
		<div class="flex flex-col gap-2 p-2 px-4">
			<div class="flex flex-col gap-2">
				<h2 class="text-left text-lg font-semibold tracking-tight">Patrón</h2>
				<ToggleGroup.Root
					type="single"
					spacing={0}
					value={filters.listingType ?? ''}
					variant="outline"
					class="border-input isolate flex w-full overflow-hidden rounded-lg border shadow-none **:data-[state=on]:border-primary **:data-[state=on]:bg-primary/15 **:data-[state=on]:text-primary"
					onValueChange={(v) => {
						if (!v) {
							filters.setListingType(null);
						} else {
							filters.setListingType(v as ListingTypeFilter);
						}
					}}
				>
					<ToggleGroup.Item
						value="con_patron"
						aria-label="Con patrón"
						class="hover:bg-accent hover:text-accent-foreground flex h-11 min-w-0 flex-1 items-center justify-center rounded-none px-3 text-base font-medium transition-colors data-[state=on]:z-10 data-[state=on]:border data-[state=on]:border-primary data-[state=on]:shadow-none sm:h-10 sm:text-sm"
					>
						Con patrón
					</ToggleGroup.Item>
					<ToggleGroup.Item
						value="sin_patron"
						aria-label="Sin patrón"
						class="hover:bg-accent hover:text-accent-foreground flex h-11 min-w-0 flex-1 items-center justify-center rounded-none px-3 text-base font-medium transition-colors data-[state=on]:z-10 data-[state=on]:border data-[state=on]:border-primary data-[state=on]:border-l data-[state=on]:shadow-none sm:h-10 sm:text-sm"
					>
						Sin patrón
					</ToggleGroup.Item>
				</ToggleGroup.Root>

				{#if filters.listingType === 'con_patron'}
					<div
						class="border-input flex min-h-11 items-center gap-2 rounded-lg border py-2 pr-1.5 pl-3"
					>
						<Checkbox
							id="filter-experiencias"
							class="shrink-0"
							checked={filters.experiencesOnly}
							onCheckedChange={(v) => filters.setExperiencesOnly(v === true)}
						/>
						<Label
							for="filter-experiencias"
							class="min-w-0 flex-1 cursor-pointer text-sm leading-snug font-medium"
						>
							Experiencias
						</Label>
						<Popover.Root>
							<Popover.Trigger>
								{#snippet child({ props })}
									<button
										type="button"
										class="text-kbblue hover:bg-accent focus-visible:ring-ring flex size-10 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:ring-2 focus-visible:outline-none"
										aria-label={EXPERIENCES_FILTER_HELP.title}
										{...props}
									>
										<CircleQuestionMark class="size-5" />
									</button>
								{/snippet}
							</Popover.Trigger>
							<Popover.Content
								side={sidebar.isMobile ? 'bottom' : 'top'}
								align={sidebar.isMobile ? 'center' : 'end'}
								sideOffset={8}
								class="z-100 w-[min(18rem,calc(100vw-2rem))] max-w-sm p-3 text-sm shadow-lg"
							>
								<p class="leading-snug font-semibold">{EXPERIENCES_FILTER_HELP.title}</p>
								<p class="text-muted-foreground mt-2 leading-relaxed">
									{EXPERIENCES_FILTER_HELP.description}
								</p>
							</Popover.Content>
						</Popover.Root>
					</div>
				{/if}
			</div>

			<div class="flex flex-col gap-2">
				<h2 class="text-left text-lg font-semibold tracking-tight">Dónde y cuándo</h2>
				<div class="flex min-w-0 flex-col gap-2">
					<Select.Root
						type="single"
						value={selectedComunidad?.slug ?? ALL}
						onValueChange={(v) =>
							navigateToLocation(v === ALL ? null : (comunidadBySlug(v) ?? null))}
					>
						<Select.Trigger class="w-full" aria-label="Comunidad">
							{selectedComunidad?.name ?? 'Toda España'}
						</Select.Trigger>
						<Select.Content>
							<Select.Group>
								<Select.Label>Comunidad</Select.Label>
								<Select.Item value={ALL} label="Toda España">Toda España</Select.Item>
								{#each comunidadOptions as comunidad (comunidad.slug)}
									<Select.Item value={comunidad.slug} label={comunidad.name}>
										{comunidad.name}
									</Select.Item>
								{/each}
							</Select.Group>
						</Select.Content>
					</Select.Root>

					{#if selectedComunidad && !selectedComunidad.uniprovincial}
						<Select.Root
							type="single"
							value={filters.province || ALL}
							onValueChange={(v) => navigateToLocation(selectedComunidad, v === ALL ? null : v)}
						>
							<Select.Trigger class="w-full" aria-label="Provincia">
								{filters.province || 'Todas las provincias'}
							</Select.Trigger>
							<Select.Content>
								<Select.Group>
									<Select.Label>Provincia</Select.Label>
									<Select.Item value={ALL} label="Todas las provincias">
										Todas las provincias
									</Select.Item>
									{#each provinciaOptions as provincia (provincia)}
										<Select.Item value={provincia} label={provincia}>{provincia}</Select.Item>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					{/if}

					{#if selectedComunidad && cityOptions.length > 0}
						<Select.Root
							type="single"
							value={filters.city ?? ALL}
							onValueChange={(v) =>
								navigateToLocation(
									selectedComunidad,
									filters.province || null,
									v === ALL ? null : v
								)}
						>
							<Select.Trigger class="w-full" aria-label="Ciudad">
								{filters.city ?? 'Todas las ciudades'}
							</Select.Trigger>
							<Select.Content>
								<Select.Group>
									<Select.Label>Ciudad</Select.Label>
									<Select.Item value={ALL} label="Todas las ciudades">
										Todas las ciudades
									</Select.Item>
									{#each cityOptions as city (city)}
										<Select.Item value={city} label={city}>{city}</Select.Item>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					{/if}

					{#if filters.city && portOptions.length > 0}
						<Select.Root
							type="single"
							value={filters.port?.name ?? ALL}
							onValueChange={(v) =>
								navigateToLocation(
									selectedComunidad,
									filters.province || null,
									filters.city,
									v === ALL ? null : v
								)}
						>
							<Select.Trigger class="w-full" aria-label="Puerto">
								{filters.port?.name ?? 'Todos los puertos'}
							</Select.Trigger>
							<Select.Content>
								<Select.Group>
									<Select.Label>Puerto</Select.Label>
									<Select.Item value={ALL} label="Todos los puertos">Todos los puertos</Select.Item>
									{#each portOptions as portOption (portOption)}
										<Select.Item value={portOption} label={portOption}>{portOption}</Select.Item>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					{/if}
					<div class="w-full min-w-0">
						<Popover.Root>
							<Popover.Trigger class="w-full">
								{#snippet child({ props })}
									<Button
										variant="outline"
										class={cn(
											'w-full min-w-0 justify-start gap-2',
											!filters.dateRange && !startValue && 'text-muted-foreground'
										)}
										type="button"
										{...props}
									>
										<CalendarIcon class="size-5 shrink-0 sm:size-4" />
										<span class="truncate text-left">
											{#if filters.dateRange?.start}
												{#if filters.dateRange.end}
													{df.format(filters.dateRange.start.toDate(getLocalTimeZone()))} -
													{df.format(filters.dateRange.end.toDate(getLocalTimeZone()))}
												{:else}
													{df.format(filters.dateRange.start.toDate(getLocalTimeZone()))}
												{/if}
											{:else if startValue}
												{df.format(startValue.toDate(getLocalTimeZone()))}
											{:else}
												Cualquier fecha
											{/if}
										</span>
									</Button>
								{/snippet}
							</Popover.Trigger>
							<Popover.Content class="w-auto p-0" align="start">
								<RangeCalendar
									value={filters.dateRange || undefined}
									onValueChange={(v) => {
										filters.setDateRange(v);
									}}
									onStartValueChange={(v) => {
										startValue = v;
									}}
									numberOfMonths={2}
									weekStartsOn={1}
									placeholder={filters.dateRange?.start}
								/>
								{#if filters.dateRange}
									<div class="border-t p-2">
										<Button
											variant="ghost"
											size="sm"
											class="w-full"
											type="button"
											onclick={() => {
												filters.setDateRange(undefined);
												startValue = undefined;
											}}
										>
											Limpiar fechas
										</Button>
									</div>
								{/if}
							</Popover.Content>
						</Popover.Root>
					</div>
				</div>
			</div>

			<div class="flex flex-col gap-2">
				<div class="flex flex-col gap-2">
					<h4 class="text-md text-left font-semibold tracking-tight">Tipo de barco</h4>
					<ToggleGroup.Root
						type="single"
						orientation="vertical"
						spacing={2}
						value={filters.boatType || undefined}
						variant="outline"
						class="**:data-[state=on]:border-primary **:data-[state=on]:bg-primary/20 **:data-[state=on]:text-primary box-border w-full max-w-full min-w-0"
						onValueChange={(value) =>
							filters.setBoatType(value === '' ? null : (value as 'velero' | 'motora'))}
					>
						<ToggleGroup.Item
							value="velero"
							aria-label="Velero"
							class="hover:bg-accent hover:text-accent-foreground w-full max-w-full min-w-0 justify-start px-2 py-2 text-base font-medium transition-all data-[state=on]:shadow-sm sm:text-sm"
						>
							<Sailboat class="mr-2 size-5 shrink-0 sm:size-4" /> Velero
						</ToggleGroup.Item>
						<ToggleGroup.Item
							value="motora"
							aria-label="Motora"
							class="hover:bg-accent hover:text-accent-foreground w-full max-w-full min-w-0 justify-start px-2 py-2 text-base font-medium transition-all data-[state=on]:shadow-sm sm:text-sm"
						>
							<Ship class="mr-2 size-5 shrink-0 sm:size-4" /> Motora
						</ToggleGroup.Item>
					</ToggleGroup.Root>

					<h4 class="text-md text-left font-semibold tracking-tight">Pasajeros</h4>
					<div class="w-full min-w-0 space-y-2">
						<Slider
							value={filters.passengersRange}
							onValueChange={(value) => filters.setPassengersRange(value as [number, number])}
							type="multiple"
							min={1}
							max={20}
							step={1}
							class="w-full"
						/>
						<div class="text-muted-foreground flex justify-between text-sm">
							<span
								>{filters.passengersRange[0]} persona{filters.passengersRange[0] !== 1
									? 's'
									: ''}</span
							>
							<span
								>{filters.passengersRange[1]} persona{filters.passengersRange[1] !== 1
									? 's'
									: ''}</span
							>
						</div>
					</div>

					<h4 class="text-md text-left font-semibold tracking-tight">Precio por día</h4>
					<div class="w-full min-w-0 space-y-2">
						<Slider
							value={filters.priceRange}
							onValueChange={(value) => filters.setPriceRange(value as [number, number])}
							type="multiple"
							min={0}
							max={2000}
							step={10}
							class="w-full"
						/>
						<div class="text-muted-foreground flex justify-between text-sm">
							<span>{filters.priceRange[0]}€</span>
							<span>{filters.priceRange[1]}€</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		{#if filters.isDirty}
			<div class="bg-background sticky bottom-0 border-t p-2">
				<div class="flex flex-col gap-2">
					<Button type="button" onclick={handleApplyFilters} class="w-full">
						Aplicar filtros ({filters.dirtyCount} cambios)
					</Button>
					<Button type="button" onclick={handleResetFilters} variant="outline" class="w-full">
						<RotateCcw class="mr-2 h-4 w-4" />
						Restablecer filtros
					</Button>
				</div>
			</div>
		{/if}
	</Sidebar.Content>
</Sidebar.Root>
